import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteActions } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';
import { selectSoilAnalysesReport } from 'src/app/soil-analysis/store/soil-analysis.selectors';
import { getCropYear, getReportSort } from 'src/app/store/selectors';

import { ReportSortService } from '../services/report-sort.service';

@Component({
  selector: 'app-soil-analysis-report',
  templateUrl: './soil-analysis-report.component.html',
  styleUrls: ['./../reports.scss'],
  standalone: false,
})
export class SoilAnalysisReportComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  protected readonly router = inject(Router);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  subscription = new Subscription();
  reportSort$ = this.store.pipe(select(getReportSort));
  cropYear$ = this.store.pipe(select(getCropYear));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));

  cropFieldsWithSoilAnalyses$ = this.store.pipe(
    select(selectSoilAnalysesReport),
    map((cropFields) => {
      cropFields.forEach((cropField) => {
        cropField.soilAnalyses.sort((a, b) =>
          a.description.toLowerCase() > b.description.toLowerCase() ? 1 : -1,
        );
        cropField.soilAnalyses.forEach((analysis) => {
          analysis.sampledCharacteristics.sort((a, b) =>
            a.designation.toLowerCase() > b.designation.toLowerCase() ? 1 : -1,
          );
        });
      });
      return cropFields;
    }),
  );

  sortedCropFields$ = combineLatest([this.cropFieldsWithSoilAnalyses$, this.reportSort$]).pipe(
    map(([cropFields, reportSort]) =>
      this.sortService.sort({
        elements: cropFields,
        option: reportSort.option,
        descending: reportSort.descending,
        cropFieldNameFunc: (f) => f.name,
        cropNameFunc: (f) => f.cropName,
      }),
    ),
  );

  today = new Date();

  buttonItems$ = this.sortService.getButtonItems();

  ngOnInit() {
    this.subscription.add(
      combineLatest([this.customer$, this.cropYear$]).subscribe(([customer, year]) => {
        const pageTitle = `${customer?.name ? customer.name : ''} ${this.locale.translate(
          'soilAnalysesReportTitle',
        )} ${year}`;
        this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
      }),
    );
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  print() {
    if (this.window) {
      this.window.print();
    }
  }

  back() {
    this.router.navigate(['/']);
  }

  buttonAction(data: string) {
    this.sortService.handleButtonAction(data);
  }
}
